import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useFormikContext, getIn } from "formik";
import {
  TextField,
  Dropdown,
  BooleanField,
  GroupField,
  ArrayField,
  FieldLabel,
  FeedbackLabel,
} from "react-invenio-forms";
import { Button, Form, Icon } from "semantic-ui-react";
import axios from "axios";

export const emptyMultiLineValue = (properties = {}) => {
  // Safeguard: If properties is undefined, we don't try to create an object
  if (!properties || typeof properties !== "object") {
    return {};
  }

  const newObject = {};
  Object.keys(properties).forEach((key) => {
    newObject[key] = "";
  });
  return newObject;
};


const getVocabularyOptions = async (vocabulary_id) => {
  // Return a promise instead of using async/await
  const response = await axios.get(`/api/vocabularies/${vocabulary_id}`)
  const options = response?.data?.hits?.hits.map((item) => ({
    title_l10n: item.title.en,
    id: { "id": item.id, "title": item.title }
  }))
  return options;
};



const MultiNestedInput = ({
  fieldPath,
  label,
  placeholder,
  description,
  additionLabel,
  icon,
  required,
  properties, // New prop for dynamic fields
}) => {
  const { values, setFieldValue, errors } = useFormikContext(); // Access Formik's context and methods
  const [vocabularyOptions, setVocabularyOptions] = useState({}); // State to store vocabulary options
  const [loading, setLoading] = useState(false);

  const hasError = fieldPath.split('.').reduce((acc, key) => {
    return acc && acc.hasOwnProperty(key) ? acc[key] : false;
  }, errors);

  // Fetch vocabulary options based on vocabulary_id
  const fetchVocabularyOptions = (vocabulary_id, propertyKey) => {
    getVocabularyOptions(vocabulary_id)
      .then((options) => {
        setVocabularyOptions((prevState) => ({
          ...prevState,
          [propertyKey]: options, // Store options for each vocabulary field
        }));
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load vocabulary options: ', error);
      });
  };

  // Fetch vocabulary options when the component mounts or when properties change
  useEffect(() => {
    Object.keys(properties).forEach((key) => {
      const property = properties[key];
      if (property.type === 'vocabulary' && property.vocabulary_id) {
        setLoading(true);
        fetchVocabularyOptions(property.vocabulary_id, key);
        setLoading(false);
      }
    });
  }, [properties]); // Re-run when properties change

  const getValueForLine = (indexPath) => {
    const currentValue = getIn(values, fieldPath, []);
    return currentValue && currentValue[indexPath] ? currentValue[indexPath] : emptyMultiLineValue(properties);
  };

  const renderField = (propertyKey, fieldPathPrefix, lineValue, property) => {
    const { label, description, type, vocabulary_id } = property;
    const options = vocabularyOptions[propertyKey] || []; // Get options from state
    switch (type) {
      case "vocabulary":
        if (loading) {
          return (
            <b>Loading...</b>
          )
        }
        return (
          <Form.Field key={propertyKey} className="sixteen wide">
            <Dropdown
              fieldPath={`${fieldPathPrefix}.${propertyKey}`}
              label={label}
              options={options} // Pass the fetched options for the dropdown
              placeholder={placeholder || `Select ${label}`}
              required={required}
              multiple={false}
              clearable={true}
              key={fieldPathPrefix + options}
            />
            {description && (
              <label className="helptext sixteen wide field">
                {description}
              </label>
            )}
          </Form.Field>
        );

      case "boolean":
        // Render BooleanField for "boolean" type
        return (
          <Form.Field key={propertyKey} className="sixteen wide">
            <BooleanField
              fieldPath={`${fieldPathPrefix}.${propertyKey}`}
              label={label}
              required={required}
            />
            {description && (
              <label className="helptext sixteen wide field">
                {description}
              </label>
            )}
          </Form.Field>
        );

      default:
        // Default: render TextField for other types
        return (
          <Form.Field key={propertyKey} className="sixteen wide">
            <TextField
              fieldPath={`${fieldPathPrefix}.${propertyKey}`}
              label={label}
              required={required}
              placeholder={placeholder || `Enter ${label}`}
              value={lineValue[propertyKey]}
              onChange={(e) =>
                setFieldValue(
                  `${fieldPathPrefix}.${propertyKey}`,
                  e.target.value,
                )
              }
            />
            {description && (
              <label className="helptext sixteen wide field">
                {description}
              </label>
            )}
          </Form.Field>
        );
    }
  };

  return (
    <div className={hasError ? "error field" : ""}>
      <ArrayField
        fieldPath={fieldPath}
        label={<FieldLabel htmlFor={fieldPath} icon={icon} label={label} />}
        addButtonLabel={additionLabel || "Add Line"}
        defaultNewValue={emptyMultiLineValue(properties)}
        required={required}
        helpText={description}
      >
        {({ arrayHelpers, indexPath }) => {
          const fieldPathPrefix = `${fieldPath}.${indexPath}`;
          const lineValue = getValueForLine(indexPath);

          return (
            <GroupField optimized key={fieldPathPrefix} >
              <div style={{ fontWeight: "bold", marginBottom: "10px" }}>
                {properties._object_name || "Object Name"}
              </div>
              <div className="sixteen wide field flow-wrap nested-indentation">
                {/* Loop through each property and render input fields */}
                {Object.keys(properties).map((propertyKey) => {
                  if (propertyKey === "_object_name") return null;
                  const property = properties[propertyKey];
                  return renderField(
                    propertyKey,
                    fieldPathPrefix,
                    lineValue,
                    property,
                  );
                })}
              </div>
              <Form.Field style={{ marginTop: "1.75rem", float: "right" }}>
                <Button
                  aria-label="Remove line"
                  className="close-btn right"
                  icon
                  onClick={() => arrayHelpers.remove(indexPath)}
                >
                  <Icon name="close" />
                </Button>
              </Form.Field>
            </GroupField>
          );
        }}
      </ArrayField>
      {hasError &&
        <FeedbackLabel
          errorMessage={hasError}
          pointing="above"
          fieldPath={fieldPath}
        />
      }

      {description && <label className="helptext">{description}</label>}
    </div>
  );
};

MultiNestedInput.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  description: PropTypes.string,
  additionLabel: PropTypes.string,
  icon: PropTypes.string,
  required: PropTypes.bool,
  properties: PropTypes.object.isRequired, // Accept dynamic properties
};

MultiNestedInput.defaultProps = {
  additionLabel: "Add Line", // Default add button label
  icon: undefined,
  required: false,
  description: undefined,
};

export default MultiNestedInput;
